import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Job from './Job'
import UsePagination from '../../components/pagination'
import JobPostModal from '../common/JobPosts/JobPostModal'

import {
  selectSearchJobsLoading,
  selectSearchJobsPagination,
  selectSearchJobsError,
} from '../../routing/fetchingfunction/jobSearch/jobSearch.reducer'
import { searchJobs } from '../../routing/fetchingfunction/jobSearch/jobSearch.actions'

import { getPaginationTotalLength } from '../../helpers'
import getJobListFilters from '../../helpers/getJobListFilters'
import { Loader } from '../../components/UI'

// Error message component for search failures
const ErrorMessage = () => (
  <div className="w-full p-6 my-4 rounded-lg bg-red-50 border border-red-100">
    <div className="flex flex-col items-center justify-center text-center">
      <h3 className="text-lg font-semibold text-red-700 mb-2">Unable to load job listings</h3>
      <p className="text-red-600 mb-4">
        We're experiencing some technical difficulties. Please try again later.
      </p>
      <button
        className="px-4 py-2 bg-primary-button text-white font-medium rounded-lg hover:bg-primary-button-hover transition-colors"
        onClick={() => window.location.reload()}
      >
        Retry
      </button>
    </div>
  </div>
)

const JobsList = ({ search, filters }) => {
  const dispatch = useDispatch()

  const [job, setJob] = useState(null)
  const [isJobPostModalOpen, setIsJobPostModalOpen] = useState(false)

  const { jobs, jobsPage, jobsTotal, jobsPageSize } = useSelector(selectSearchJobsPagination)
  const loading = useSelector(selectSearchJobsLoading)
  const error = useSelector(selectSearchJobsError)

  const handlePageChange = (_, value) => {
    if (value !== jobsPage) {
      dispatch(
        searchJobs({
          page: value,
          ...getJobListFilters(search, filters),
        })
      )
    }
  }

  const handleOpenJobDetailsModal = (job) => {
    setJob(job)
    setIsJobPostModalOpen(true)
  }

  const handleCloseJobDetailsModal = () => {
    setIsJobPostModalOpen(false)

    setTimeout(() => {
      setJob(null)
    }, 400)
  }

  useEffect(() => {
    dispatch(
      searchJobs({
        page: 1,
        ...getJobListFilters(search, filters),
      })
    )
  }, [filters, search])

  const jobsContent = jobs.map((job) => {
    return <Job key={job.job_hash} job={job} onDetails={handleOpenJobDetailsModal} />
  })

  // Show error message when there's an error and not loading
  if (error && !loading) {
    return <ErrorMessage />
  }

  return (
    <div className="w-full flex flex-col items-start">
      {jobs.length ? (
        <div className="w-full flex flex-col items-start gap-y-[10px]">{jobsContent}</div>
      ) : (
        loading && (
          <div className="w-full flex items-center justify-center">
            <Loader className="text-white" />
          </div>
        )
      )}

      {/* Show message when no jobs found and not loading */}
      {!jobs.length && !loading && (
        <div className="w-full p-6 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-center text-gray-600">No jobs found matching your criteria.</p>
        </div>
      )}

      {jobsTotal > jobsPageSize && (
        <div className="w-full flex items-center justify-center">
          <UsePagination
            count={getPaginationTotalLength(jobsTotal, jobsPageSize)}
            page={jobsPage}
            onChange={handlePageChange}
            loading={loading}
            altColors
          />
        </div>
      )}

      <JobPostModal job={job} isOpen={isJobPostModalOpen} onClose={handleCloseJobDetailsModal} />
    </div>
  )
}

export default JobsList
